/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useState, useEffect, useContext, useMemo, useRef} from 'react'
import {useIntl} from 'react-intl'

import {PageLink, PageTitle, useLayout} from '../../../_fds/layout/core'
import {LaborSchedulerHeader} from './LaborSchedulerHeader'
import {Scheduler} from './components/Scheduler'
import {EVENTS, RECOURCES} from './events'
import {Overview} from './components/Overview'
import {TeamMembers} from './components/TeamMembers'
import {AvailabilityView} from './availability/AvailabilityView'
import {
  getLaborScheduleActual,
  createShift,
  getWeatherForecast,
  deleteShift,
  updateShift,
  getHoursOfOperations,
  getWeeklyShifts,
  getEmployees,
  getLaborPositions,
  getAllowableHoursDaily,
  getHeadcountBoundsDaily,
  listEmployeeExceptions,
  listLocationAvailability,
} from './core/_requests'
import {LaborScheduleActualModel, GroupModel} from './core/_models'

import {
  comparePositions,
  convertAvailablePositionsToOptions,
  convertBaseEmployeesToAssigneeOptions,
  convertBaseEmployeesToResources,
  getEventColor,
  getPositionDisplayName,
  setPositionsMeta,
  convertHoursOfOperations,
  convertLaborShifts,
  DAYS_OF_WEEK,
  extractEmployeeHours,
  extractHoursByDay,
  extractLaborCostByDay,
  filterEventsBetween,
  getAllowableValues,
  getBusinessDay,
  getHoursOfOperationOnDay,
  getMinMaxHoursOfOperations,
  WeekDays,
  withUnassignedShiftRows,
} from './helpers/generals'
import {
  applyExceptionsToLookup,
  buildAvailabilityLookup,
  toDateKey,
  weekDatesFrom,
} from './helpers/availability'
import {Navigate, Routes, Route, Outlet, Link, useLocation} from 'react-router-dom'
import LaborScheduleActualContext, {
  LaborScheduleActualProvider,
} from './context/LaborScheduleActualContext'
import SelectedLocationIdContext from './context/SelectedLocationContext'
import LoadingStateContext from './context/LoadingContext'

import {getLocations} from '../scheduler/core/_requests'
import {Navigation} from './components/nav/Navigation'
import {LocationSelect} from './components/common/LocationSelect'
import {Form} from 'react-bootstrap'
import {useStore} from './store'
import Editor from './components/views/Editor'
import {useAuth, hasPermission, canManageLockedSchedules, LABOR_SCHEDULING_PERMS} from '../auth'
import {EventActions, ProcessedEvent, SchedulerProps} from './types'
import {addDays, addMinutes, eachMinuteOfInterval, getWeek, startOfWeek} from 'date-fns'
import {useToast} from '../../../_fds/layout/components/toast-messages'
import {ScheduleCompletionContainer} from './components/common/ScheduleCompletionContainer'
import {SchedulerDropdown} from './components/SchedulerDropdown'
import {FdsModal} from './components/common/FdsModal'
import {fi} from 'date-fns/locale'
import {LaborSchedulerHeaderTest} from './LaborSchedulerHeaderTest'
import {TemplateModeToggle} from './components/template/TemplateModeToggle'
import {TemplateHeader} from './components/template/TemplateHeader'
import {useTemplateModeEffects} from './components/template/useTemplateMode'
import {RecommendedScheduleModal} from './components/proposal/RecommendedScheduleModal'
import {useRecommendedScheduleEffects} from './components/proposal/useRecommendedSchedule'
import {generateEventId} from './helpers/templates'

const laborSchedulingBreadCrumbs: Array<PageLink> = [
  {
    title: 'Labor Scheduling',
    path: '/labor-scheduling/scheduling/schedule',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

type CustomToolbarProps = {
  locations: GroupModel[] | null
  laborScheduleActual: LaborScheduleActualModel | null
  setLaborScheduleActual: (laborSchedule: LaborScheduleActualModel | null) => void
  selectedLocationId: number | null
  setSelectedLocationId: (id: number | null) => void
}

const CustomToolbarElements: FC<CustomToolbarProps> = ({
  locations,
  laborScheduleActual,
  setLaborScheduleActual,
  selectedLocationId,
  setSelectedLocationId,
}) => {
  return (
    <>
      <div
        className='d-flex flex-row flex-grow-1 flex-wrap justify-content-end'
        style={{marginLeft: '10px'}}
      >
        <div className='d-flex flex-wrap align-items-center '>
          <TemplateModeToggle />
          <div className='vr mx-2'></div>
          {/* begin::Label */}
          <Form.Label className='fs-7 fw-bold text-gray-700 flex-shrink-0 pe-4 d-none d-md-block'>
            Store:
          </Form.Label>
          {/* end::Label */}
          {/* begin::Select */}
          <LocationSelect locations={locations} />
          {/* end::Select */}
          {/* begin::Actions */}
          <Navigation />
          {/* end::Actions */}
          <div className='vr mx-2'></div>
          <SchedulerDropdown
            laborScheduleActual={laborScheduleActual}
            setLaborScheduleActual={setLaborScheduleActual}
          />
        </div>
      </div>
    </>
  )
}

function CreateSchedulePage() {
  const {currentUser} = useAuth()
  const {
    selectedLocationId,
    dialog,
    resources,
    events,
    hoursOfOperations,
    view,
    selectedDate,
    fields,
    templateMode,
    handleState,
  } = useStore()
  const {showToast} = useToast()
  const [minStartHour, maxEndHour] = getMinMaxHoursOfOperations(hoursOfOperations)
  useTemplateModeEffects()
  // Asks the backend whether this store has a machine-generated schedule worth
  // showing, and opens the prompt once per proposal.
  useRecommendedScheduleEffects()

  // Read through a ref inside handleConfirm: the store captures onConfirm once
  // at Scheduler mount, so the closure's own context value is permanently the
  // first render's (null).
  const {laborScheduleActual, setLaborScheduleActual} = useContext(LaborScheduleActualContext)
  const laborScheduleRef = useRef(laborScheduleActual)
  laborScheduleRef.current = laborScheduleActual

  const handleConfirm = async (
    event: ProcessedEvent,
    action: EventActions
  ): Promise<ProcessedEvent> => {
    // Template mode: the draft is purely client-side until saved — no remote
    // call, just mark it dirty and let confirmEvent update the store.
    if (templateMode) {
      handleState(true, 'templateDirty')
      return {
        ...event,
        event_id: event.event_id || generateEventId(),
        businessDay: event.businessDay,
      }
    }
    return new Promise(async (res, rej) => {
      try {
        const createdShift = await createShift(event, event.businessDay) // Call createShift with await to handle potential errors
        // Mirror the backend side effect: the first shift flips an open
        // schedule to in_progress — keep the badge/options in sync without a
        // refetch.
        const schedule = laborScheduleRef.current
        if (schedule?.workflow_status === 'open') {
          setLaborScheduleActual({...schedule, workflow_status: 'in_progress'})
        }
        setTimeout(() => {
          res({
            ...event,
            event_id: createdShift?.data?.id,
            businessDay: event.businessDay,
          })

          showToast('Shift Created', 'Shift created successfully!', 'success')
        }, 10)
      } catch (error) {
        showToast('Shift Creation Failed', 'Unable to create shift!', 'danger')
        setTimeout(() => {}, 10)
        rej(error)
      }
    })
  }
  const handleDelete = async (id: number): Promise<number> => {
    if (templateMode) {
      handleState(true, 'templateDirty')
      return id
    }
    return new Promise(async (res, rej) => {
      try {
        await deleteShift(id)
        res(id)
        showToast('Shift Deleted', 'Shift deleted successfully!', 'success')
      } catch (error) {
        showToast('Shift Deletion Failed', 'Unable to delete shift!', 'danger')
        rej(error)
      }
    })
  }
  const handleEventDrop = async (
    time: Date,
    businessDay: Date,
    updated: ProcessedEvent
  ): Promise<ProcessedEvent> => {
    if (templateMode) {
      handleState(true, 'templateDirty')
      return updated
    }
    return new Promise(async (res, rej) => {
      try {
        await updateShift(updated, businessDay)
        setTimeout(() => {
          res(updated)
        }, 10)
        showToast('Shift Updated', 'Shift updated successfully!', 'success')
      } catch (error) {
        showToast('Shift Update Failed', 'Unable to update shift!', 'danger')
        rej(error)
      }
    })
  }
  const handleEventClick = async (event: ProcessedEvent) => {}

  const location = useLocation()

  const createdPage = useMemo(() => {
    // console.log('CREATED PAGE USE MEMO', location)
    return (
      <Routes>
        <Route
          element={
            <>
              {templateMode ? <TemplateHeader /> : <LaborSchedulerHeader />}
              {dialog && <Editor />}
              <Outlet />
            </>
          }
        >
          <Route
            path='schedule'
            element={
              <>
                {/* <FdsSelectBox value={selectedLocationId} 
                onChange=((newValue: any) =>  setselectedLocationId(newValue))
                options={locations} label='test' labelKey='text' /> */}

                <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
                  <Scheduler
                    events={events}
                    selectedLocationId={selectedLocationId}
                    dialogMaxWidth='sm'
                    editable={true}
                    deletable={true}
                    draggable={true}
                    height={150}
                    selectedDate={selectedDate}
                    view={view}
                    onConfirm={handleConfirm}
                    onDelete={handleDelete}
                    onEventDrop={handleEventDrop}
                    onEventClick={handleEventClick}
                    week={{
                      weekDays: [0, 1, 2, 3, 4, 5, 6],
                      weekStartOn: DAYS_OF_WEEK[currentUser?.company?.week_start || 'monday'] ?? 1,
                      startHour: minStartHour,
                      endHour: maxEndHour,
                      step: 30,
                    }}
                    month={{
                      weekDays: [0, 1, 2, 3, 4, 5, 6],
                      weekStartOn: DAYS_OF_WEEK[currentUser?.company?.week_start || 'monday'] ?? 1,
                      startHour: 7,
                      endHour: 15,
                    }}
                    navigation={true}
                    navigationPickerProps={{
                      shouldDisableDate(day) {
                        return false
                      },
                    }}
                    resources={resources}
                    resourceFields={{
                      idField: 'employee_id',
                      textField: 'title',
                      subTextField: 'position_name',
                      avatarField: 'avatar',
                      colorField: 'color',
                    }}
                    resourceViewMode='tabs'
                    fields={fields}
                  />
                </div>
              </>
            }
          />
          <Route
            path='overview'
            element={
              <>
                <Overview />
              </>
            }
          />
          <Route
            path='team_members'
            element={
              <>
                <TeamMembers />
              </>
            }
          />
          <Route
            path='availability'
            element={
              <>
                <AvailabilityView />
              </>
            }
          />
          <Route index element={<Navigate to='/labor-scheduling/scheduling/schedule' />} />
        </Route>
      </Routes>
    )
    // <Scheduler> consumes its props ONLY at mount (useStore(props) merges in
    // a []-deps effect); live values flow through the store, so rebuilding the
    // element tree per data/date/view change is pure waste. location.pathname
    // stays: tab-away/back remounts Scheduler and re-seeds the store from the
    // element's props — a stale element would seed stale defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog, templateMode, location.pathname])

  // Outside the memo: the modal owns its own open/loading state and must not be
  // torn down and remounted every time the events list changes.
  return (
    <>
      {createdPage}
      <RecommendedScheduleModal />
    </>
  )
}

const LaborSchedulingWrapper: FC = () => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const {setToolbarType, setCustomToolbarElements} = useLayout()
  const [laborSchedule, setLaborSchedule] = useState<LaborScheduleActualModel | null>(null)
  const [weatherForecast, setWeatherForecast] = useState<string | null>(null)
  const [locations, setLocations] = useState<GroupModel[] | null>(null)
  const {
    handleState,
    handleMultipleStates,
    events,
    selectedLocationId,
    selectedDate,
    selectedWeekStart,
    selectedPosition,
    employees,
    week,
    hoursOfOperations,
    allowableHoursDaily,
    headcountBoundsDaily,
    resources,
    availablePositions,
    templateMode,
    availabilityRevision,
  } = useStore()
  const [isSchedulingEnabled, setIsSchedulingEnabled] = useState(true)

  const [minStartHour, maxEndHour] = getMinMaxHoursOfOperations(hoursOfOperations)

  // The company's week start rides along in the auth payload — no fetch, known
  // at mount. Everything week-keyed reads it from here rather than from the
  // store's `week`, which starts life on the DEFAULT (Saturday) week and only
  // becomes right when the hours-of-operation response rebuilds it. Deriving
  // selectedWeekStart from that default meant every week-keyed request went out
  // twice on a cold load — shifts (44 kB), the schedule, allowable hours
  // (39 kB) and the measure bounds (24 kB, the slowest call on the page) for a
  // Saturday-aligned week that was thrown away seconds later.
  const companyWeekStartOn = (DAYS_OF_WEEK[currentUser?.company?.week_start || 'monday'] ??
    1) as WeekDays

  // Keep the store's `week` on that same alignment from the first render, so
  // the header and the gauges don't lay out a Saturday week either. The real
  // week (with the location's opening hours) replaces this when it lands.
  useEffect(() => {
    if (!week || week.weekStartOn === companyWeekStartOn) return
    handleState({...week, weekStartOn: companyWeekStartOn}, 'week')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyWeekStartOn])

  const _setSelectedLocationId = (locationId: number | null) => {
    handleState(locationId, 'selectedLocationId')
  }

  const checkStritclyWrongSchedule = () => {
    const {weekStartOn, weekDays, step} = week!
    const _weekStart = startOfWeek(selectedDate, {weekStartsOn: weekStartOn})
    const daysList = weekDays.map((d) => addDays(_weekStart, d))
    let tempStriclyWrongSchedule = false
    daysList.forEach((day) => {
      const [dayStart, dayEnd] = getHoursOfOperationOnDay(day, hoursOfOperations)

      const dayEvents = events.filter(
        (x) => getBusinessDay(x.start, hoursOfOperations).getDate() === day.getDate()
      )

      const START_TIME = dayStart
      const END_TIME = addMinutes(dayEnd, -30)
      const hours = eachMinuteOfInterval(
        {
          start: START_TIME,
          end: END_TIME,
        },
        {step: step}
      )

      const allowableValues = getAllowableValues(
        allowableHoursDaily,
        day,
        selectedPosition,
        hours,
        headcountBoundsDaily
      )

      let recousedEvents =
        selectedPosition === 'all'
          ? dayEvents
          : dayEvents.filter((e) =>
              resources
                .filter((emp) => emp.position === selectedPosition)
                .some((emp) => emp.employee_id === e.employee_id)
            )

      if (allowableValues.length !== 0) {
        hours.map((h, i) => {
          const headCountsUsed = filterEventsBetween(
            recousedEvents,
            h,
            addMinutes(h, step),
            undefined,
            true,
            availablePositions
          ).length
          const maxHeadCounts = allowableValues[i].max_headcount
          const minHeadCounts = allowableValues[i].min_headcount

          if (
            (headCountsUsed > maxHeadCounts || headCountsUsed < minHeadCounts) &&
            allowableValues[i].is_strict
          ) {
            tempStriclyWrongSchedule = true
          }
        })
      }
    })
    return tempStriclyWrongSchedule
  }

  const _setEditorFields = (resourceOptions: any, availablePositions: any) => {
    const fields = [
      {
        name: 'employee_id',
        type: 'select',
        // Optional since template support: applied-template shifts start
        // unassigned and may be edited/kept without an employee.
        config: {label: 'Assignee', required: false},
        options: resourceOptions,
      },
      {
        name: 'position',
        type: 'select',
        config: {label: 'Position', required: true},
        // default: [1],
        options: convertAvailablePositionsToOptions(availablePositions),
      },
    ]
    return fields
  }

  const calculateScheduleEnabling = (_laborScheduleActual: LaborScheduleActualModel) => {
    // if selected date's week is before current week or after 4 weeks from current week, disable scheduling where weekStartOn is 2
    const currentWeek = getWeek(new Date(), {weekStartsOn: week?.weekStartOn})
    const selectedWeek = getWeek(selectedDate, {weekStartsOn: week?.weekStartOn})
    // The shift-edit lock is decided server-side from the workflow config and
    // THIS user's role (honoring impersonation + FDS admin) and returned as
    // can_edit_shifts — it replaces the old client status guess + role check,
    // which is exactly what let a locked schedule stay editable when the client
    // status was stale. The 4-week window remains a client-side policy;
    // managers who can edit locked schedules also bypass the window.
    const canEditLocked = Boolean(_laborScheduleActual?.can_edit_shifts)
    const inWindow = selectedWeek >= currentWeek && selectedWeek <= currentWeek + 3
    const enableScheduling = canEditLocked && (inWindow || canManageLockedSchedules(currentUser))

    // Permissions gate on top of the week-window/status logic: view-only
    // users get a fully read-only board regardless of role or week.
    // Cell-level shift creation keys off `draggable`, so it includes
    // `create`; the event editor and delete actions strictly need `edit`
    // (deletion is modeled as an edit on the backend).
    const canCreateShifts = hasPermission(currentUser, LABOR_SCHEDULING_PERMS.create)
    const canEditShifts = hasPermission(currentUser, LABOR_SCHEDULING_PERMS.edit)

    const scheduling = Boolean(enableScheduling) && (canCreateShifts || canEditShifts)
    setIsSchedulingEnabled(scheduling)

    return new Map<string, any>([
      ['draggable', scheduling],
      ['deletable', scheduling && canEditShifts],
      ['editable', scheduling && canEditShifts],
    ])
  }

  useEffect(() => {
    // Template mode suspends every schedule-data effect: the store's events/
    // resources hold the client-side draft and must not be recomputed,
    // refetched, or clobbered until the snapshot is restored on exit.
    if (templateMode) return
    const _employeeHours = extractEmployeeHours(events)
    const _dailyHours = extractHoursByDay(events)
    const _dailyLaborCost = extractLaborCostByDay(events, employees)

    var states = new Map<string, any>([
      ['employeeHours', _employeeHours],
      ['dailyHours', _dailyHours],
      ['dailyLaborCost', _dailyLaborCost],
    ])

    if (hoursOfOperations !== null) {
      const _strictlyWrongSchedule = checkStritclyWrongSchedule()

      states.set('strictlyWrongSchedule', _strictlyWrongSchedule)
    }

    handleMultipleStates(states)
  }, [events])

  // The location list belongs to the user, not to the schedule, and nothing on
  // this page can change it — so fetch it ONCE. Keying this on `laborSchedule`
  // re-ran it on every schedule change (each week's fetch sets a fresh object),
  // which on a cold load meant five identical /locations/user-scoped responses
  // AND five toolbar rebuilds. It also gates the whole page: selectedLocationId
  // comes out of the first response, and the Overview chart/table requests
  // can't start until it does.
  useEffect(() => {
    document.body.setAttribute('data-fds-app-toolbar-fixed', 'true')
    setToolbarType('extended')

    const _setLocations = async () => {
      const _locations = await getLocations()
      setLocations(_locations)
      if (selectedLocationId === null || selectedLocationId === undefined) {
        _setSelectedLocationId(_locations && _locations[0].location_id)
      }
    }
    _setLocations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The toolbar still has to be rebuilt when the schedule (status badge,
  // options menu) or the selection changes — but from the list already in
  // state, without going back to the server for it.
  useEffect(() => {
    if (!locations) return

    setCustomToolbarElements(
      CustomToolbarElements({
        locations,
        laborScheduleActual: laborSchedule,
        setLaborScheduleActual: setLaborSchedule,
        selectedLocationId: selectedLocationId,
        setSelectedLocationId: _setSelectedLocationId,
      })
    )
    // Not selectedLocationId: the store selector inside LocationSelect owns
    // that, and rebuilding the toolbar for it would only churn the layout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, laborSchedule])

  // locationId change effect
  useEffect(() => {
    const _getHoursOfOperations = async () => {
      const hoursOfOperations = await getHoursOfOperations(selectedLocationId)

      const convertedHoursOfOperation = convertHoursOfOperations(hoursOfOperations)

      const [minStartHour, maxEndHour] = getMinMaxHoursOfOperations(convertedHoursOfOperation)
      const newWeek = {
        disableGoToDay: false,
        weekDays: [0, 1, 2, 3, 4, 5, 6] as WeekDays[],
        weekStartOn: DAYS_OF_WEEK[currentUser?.company?.week_start || 'monday'] ?? (1 as WeekDays),
        startHour: minStartHour,
        endHour: maxEndHour,
        step: 30,
        navigation: true,
      }
      return {
        hoursOfOperations: convertedHoursOfOperation,
        week: newWeek,
      }
    }

    async function fetchData() {
      // All three fetches run concurrently — only the CONVERSIONS below need
      // the position-metadata registry (colors, sort order, role classes), so
      // seed it after the fetches land but before any conversion runs.
      // (getEmployees resolves undefined on a failed request — fall back to
      // an empty list so the scheduler still renders.)
      const [positions, employees, _hoursOfOperations] = await Promise.all([
        getLaborPositions(selectedLocationId),
        getEmployees(selectedLocationId, selectedPosition).then((e) => e ?? []),
        _getHoursOfOperations(),
      ])
      setPositionsMeta(positions)

      // Order employees now that the registry is seeded — comparePositions
      // reads sort_order from it (rgm→sl→crew…), then first/last name. Sorting
      // in getEmployees would race the concurrent positions fetch and collapse
      // to alphabetical-by-position-key (GM/SL sinking below crew).
      const sortedEmployees = [...employees].sort(
        (a, b) =>
          comparePositions(a.position, b.position) ||
          (a.first_name || '').localeCompare(b.first_name || '') ||
          (a.last_name || '').localeCompare(b.last_name || '')
      )

      const fields = _setEditorFields(events, positions)
      const newStates = new Map<string, any>([
        ['availablePositions', positions],
        ['employees', sortedEmployees],
        ['resourceOptions', convertBaseEmployeesToAssigneeOptions(sortedEmployees)],
        ['hoursOfOperations', _hoursOfOperations.hoursOfOperations],
        ['week', _hoursOfOperations.week],
        ['fields', fields],
        ['resources', convertBaseEmployeesToResources(sortedEmployees)],
      ])
      handleMultipleStates(newStates)
    }

    if (!selectedLocationId || templateMode) return

    fetchData()
  }, [selectedLocationId])

  // Allowable hours are per-date now — refetch them for the visible week,
  // alongside the measure-derived headcount bounds the grid falls back to for
  // slots the allowable table has no figure for. Two requests rather than one
  // because the bounds go through the reporting engine: the grid should draw
  // as soon as the cheap half lands, not wait on a measure render.
  useEffect(() => {
    if (!selectedLocationId || !selectedWeekStart || templateMode) return

    const weekStart = selectedWeekStart
    const weekEnd = addDays(weekStart, 6)

    let cancelled = false
    // Both halves are pending for THIS week from here on: the completion
    // gauges hold rather than score the week against one half of its bands
    // (or, on a week change, against the previous week's).
    handleMultipleStates(
      new Map<string, any>([
        ['allowableHoursLoaded', false],
        ['headcountBoundsLoaded', false],
      ])
    )

    const _fetchAllowableHours = async () => {
      const allowableHours = await getAllowableHoursDaily(selectedLocationId, weekStart, weekEnd)
      // A response for a week the user has already navigated away from must
      // not land on top of the current one.
      if (cancelled) return
      // Data and its loaded flag in one update: two writes would emit an
      // in-between state where the gauges are told to score a week whose
      // figures haven't been stored yet.
      handleMultipleStates(
        new Map<string, any>([
          ['allowableHoursDaily', allowableHours ?? null],
          ['allowableHoursLoaded', true],
        ])
      )
    }

    const _fetchHeadcountBounds = async () => {
      const bounds = await getHeadcountBoundsDaily(selectedLocationId, weekStart, weekEnd)
      if (cancelled) return
      // undefined = the request failed; leave it null so the grid keeps the
      // allowable figures rather than implying a band of zero. The flag still
      // flips — settled, not successful — so the gauges don't wait forever.
      handleMultipleStates(
        new Map<string, any>([
          ['headcountBoundsDaily', bounds ?? null],
          ['headcountBoundsLoaded', true],
        ])
      )
    }

    _fetchAllowableHours()
    _fetchHeadcountBounds()

    return () => {
      cancelled = true
    }
  }, [selectedLocationId, selectedWeekStart])

  // Employee availability for the whole location, flattened once into a
  // per-employee typed array. It's date-irrelevant (a weekly pattern), so this
  // is keyed on the location rather than the week. One request rather than one
  // per employee, because every Cell in the grid reads it.
  //
  // Also refetched whenever the availability editor saves: it is a nested
  // route, so this component stays mounted the whole time it is open and the
  // location never changes on the way back — without the revision the board
  // would go on shading the availability that was loaded before the edit.
  useEffect(() => {
    if (!selectedLocationId || templateMode) return

    let cancelled = false
    const _fetchAvailability = async () => {
      const availability = await listLocationAvailability(selectedLocationId, true)
      if (cancelled) return
      // undefined = the request failed; leave the lookup null so the grid
      // shades nothing rather than implying everyone is fully available.
      if (!availability) {
        handleState(null, 'employeeAvailability')
        return
      }

      const lookup = buildAvailabilityLookup(availability)

      // Dated entries for the week ON SCREEN. The lookup is indexed by ISO
      // weekday and the board draws one week, so each date in view maps onto
      // one of its columns — and a date replaces that column outright, the
      // same rule the backend applies when it judges a shift. Fetched second
      // rather than folded into the list endpoint: the weekly pattern is the
      // same for every week, so only this part has to be refetched as the
      // board moves.
      const weekDates = weekDatesFrom(
        startOfWeek(selectedDate, {weekStartsOn: week?.weekStartOn ?? 1})
      )
      const exceptions = await listEmployeeExceptions(
        selectedLocationId,
        null,
        toDateKey(weekDates[0]),
        toDateKey(weekDates[6])
      )
      if (cancelled) return
      // A failed exceptions call leaves the weekly shading in place rather
      // than blanking the board: the standing pattern is still true, it is
      // only the overrides that are missing.
      handleState(
        exceptions ? applyExceptionsToLookup(lookup, exceptions, weekDates) : lookup,
        'employeeAvailability'
      )
    }

    _fetchAvailability()
    return () => {
      cancelled = true
    }
  }, [selectedLocationId, availabilityRevision, selectedDate, week?.weekStartOn])

  // locationId, week change effect
  useEffect(() => {
    let cancelled = false

    const _getShifts = async () => {
      const shifts = await getWeeklyShifts(selectedDate, selectedLocationId)
      // A failed request resolves undefined — read it as an empty week rather
      // than throwing out of `.map` on the way to the store.
      const events = convertLaborShifts(shifts ?? [])
      return events
    }

    async function fetchData() {
      try {
        const [shifts, _laborScheduleActual] = await Promise.all([
          _getShifts(),
          getLaborScheduleActual(selectedLocationId, selectedDate),
        ])
        // Stale week (the user moved on while this was in flight): drop it
        // rather than paint another week's shifts over the current one.
        if (cancelled) return
        setLaborSchedule(_laborScheduleActual)
        const states = calculateScheduleEnabling(_laborScheduleActual)
        states.set('events', shifts)
        states.set('selectedLaborScheduleId', _laborScheduleActual?.id)
        // Flipped with the events themselves — a week with zero shifts is
        // indistinguishable from an unloaded one by `events` alone.
        states.set('shiftsLoaded', true)
        handleMultipleStates(states)
      } catch (error) {
        console.log(error)
        // Settled, not successful: the gauges score whatever did load instead
        // of sitting at "—" for the rest of the session.
        if (!cancelled) handleState(true, 'shiftsLoaded')
      }
    }

    // selectedWeekStart is required, not just a dependency: this effect used to
    // run once the moment the location arrived (week start still null) and
    // again as soon as it was derived, sending the same 44 kB shifts request
    // and the same schedule lookup twice for the same week.
    if (!selectedLocationId || !selectedWeekStart || templateMode) return

    handleState(false, 'shiftsLoaded')
    fetchData()

    return () => {
      cancelled = true
    }
  }, [selectedLocationId, selectedWeekStart])

  // Employee-row visibility depends on the loaded week's shifts: an inactive or
  // hidden-in-schedule employee keeps their row only while they hold a shift
  // (old schedules), so resources must be recomputed per week. Unassigned
  // shifts (applied templates) get a pinned pseudo-row via
  // withUnassignedShiftRows.
  useEffect(() => {
    if (!employees?.length || templateMode) return
    handleState(
      withUnassignedShiftRows(convertBaseEmployeesToResources(employees, events), events),
      'resources'
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, events])

  // Re-skin loaded events when position metadata arrives: colors and display
  // names come from the registry, which can load after the first shifts fetch.
  useEffect(() => {
    if (!events?.length || templateMode) return
    handleState(
      events.map((e) => ({
        ...e,
        color: getEventColor(e.position),
        title: getPositionDisplayName(e.position),
      })),
      'events'
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePositions])

  // /labor/schedule/weekly-summary is NOT fetched here any more. Its only
  // consumer was WeeklySummaryContext -> LaborSchedulerHeader.projectedLabor,
  // a figure that was computed and then never rendered; the Projected vs Target
  // Labor card on the Overview is config-driven and fetches its own data
  // (slug projected_vs_target_labor). getWeeklyScheduleSummary and
  // WeeklySummaryContext are still there for whoever puts the number back on
  // screen — re-add a fetch keyed on (selectedLocationId, selectedDate), which
  // is all the endpoint takes, plus a refetch after shift edits.

  // selectedDate, selectedLocationId change effect: publishes the week window
  // everything else is keyed on.
  //
  // It used to also fetch /forecasting/day-slot-stats into
  // `store.scheduleDaySlotStats`. NOTHING reads that field — the summary plot
  // builds its series from the shifts and its own chart config — so the load
  // was spending up to five calls (the slowest of them 1.3 s) on a response
  // that went straight into the bin. The store field, the model and
  // getScheduleDaySlotStats are left in place for whoever wires a reader up.
  useEffect(() => {
    // Template mode: selectedDate moves onto the anchor week (day tabs) — it
    // must not derive selectedWeekStart.
    if (!selectedLocationId || templateMode) return

    setWeatherForecast('sunny')
    handleState(startOfWeek(selectedDate, {weekStartsOn: companyWeekStartOn}), 'selectedWeekStart')
    // companyWeekStartOn comes from the auth payload and never changes mid
    // session, so this now runs once per (location, date) — the old dep on the
    // store's `week.weekStartOn` re-ran it when hours-of-operation rebuilt the
    // week, re-deriving selectedWeekStart onto a different day and firing the
    // whole week-keyed fetch set a second time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, selectedDate, companyWeekStartOn])

  const wrapper = useMemo(() => {
    return (
      <>
        <LaborScheduleActualProvider
          value={{laborScheduleActual: laborSchedule, setLaborScheduleActual: setLaborSchedule}}
        >
          <PageTitle breadcrumbs={laborSchedulingBreadCrumbs}>
            {intl.formatMessage({id: 'MENU.CREATE_SCHEDULE'})}
          </PageTitle>
          <CreateSchedulePage />
        </LaborScheduleActualProvider>
      </>
    )
  }, [laborSchedule])

  return wrapper
}

export {LaborSchedulingWrapper}
